import { importedTemplateIdAtom } from '@/atoms/templateCommunityAtoms';
import { refCodeAtom, referralCampaignAtom } from '@/atoms/userCreationAtoms';
import ForceUpdate from '@/components/force-update/force-update';
import { useImportTemplate } from '@/container/community/hooks/useImportTemplate';
import UnauthenticatedDemoFlow from '@/container/demo-flow/unauthenticated-demo-flow';
import { EarlyMeetingFlow } from '@/features/early-meeting-flow';
import {
  GET_ONBOARDING_V2_VARIANT,
  IS_EARLY_MEETING_FLOW_ENABLED,
  IS_ONBOARDING_SIGNUP_BYPASS_ENABLED,
  IS_ONBOARDING_V2_ENABLED,
} from '@/features/flags/legacy-flags';
import { RedirectToAuthSignup, SignUpPage } from '@/features/onboarding-v2';
import { useManualExposure } from '@/hooks/amplitude';
import { useAuthToken } from '@/hooks/useAuthToken';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { usePosthogFlagsReady } from '@/providers/posthog-flags';
import { isTauri } from '@/tauri/utils';
import {
  getRefCodeFromUrl,
  getReferralCampaignFromUrl,
  storeRefCode,
  storeReferralCampaign,
} from '@/utils/affiliateTracking';
import { appendAttributionParamsToRedirectUrl } from '@/utils/attributionParams';
import { isOnPrem } from '@/utils/buildProfile';
import { Loader } from '@heidi/snowflake-ui/loader';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

const OnboardingPage = () => {
  const router = useRouter();
  const token = useAuthToken();
  const { isReady } = router;
  const { isAuthenticated, isLoading: isAuthLoading } = useIsAuthenticated();
  const flagsReady = usePosthogFlagsReady();
  const setRefCode = useSetAtom(refCodeAtom);
  const setReferralCampaign = useSetAtom(referralCampaignAtom);

  const importedTemplateId = useAtomValue(importedTemplateIdAtom);
  const { importTemplate } = useImportTemplate();

  const isEarlyMeetingFlow = IS_EARLY_MEETING_FLOW_ENABLED();
  const isSignupBypassEnabled = IS_ONBOARDING_SIGNUP_BYPASS_ENABLED();

  // Mirrors the redirect effect below: anyone with a session (and not returning
  // mid-flow from social login) is about to be pushed to /scribe, and anyone
  // with a pending imported template is about to be pushed into the import
  // route. They never actually see a variant, so exclude them from $exposure.
  const isReturningFromBookDemo = router.query.flow === 'book-demo';
  const willRedirectAway =
    Boolean(importedTemplateId) ||
    Boolean(token && isAuthenticated && !isReturningFromBookDemo);

  // Tauri short-circuits the flag at the variant gate below, so Tauri users
  // never actually see treatment — exclude them from $exposure to keep the
  // experiment denominator on the web cohort the flag is actually testing.
  useManualExposure({
    flagKey: 'release-early-meeting-flow',
    isEnabled: isEarlyMeetingFlow,
    isVisible: flagsReady && isReady && !isAuthLoading && !isTauri() && !willRedirectAway,
  });

  // Bypass exposure: every visitor that would reach the bypass branch on page
  // load — i.e. anyone on `/onboarding` not in Tauri or on-prem (both of which
  // the flag forces off), not in EarlyMeetingFlow (which only reaches the
  // bypass after the user clicks "Sign up free"), and not bound for a session
  // redirect. Mirrors the branch condition below. Variant-agnostic: the bypass
  // replaces both `<SignUpPage>` (V2-treatment) and `<UnauthenticatedDemoFlow>`
  // (V2-control / V2-disabled), so we count all of them in the denominator.
  // EarlyMeetingFlow can wire its own exposure later if we want to measure
  // the inner "Sign up free" cohort separately.
  const isBypassVisible = Boolean(
    flagsReady &&
    isReady &&
    !isAuthLoading &&
    !isTauri() &&
    !isOnPrem &&
    !willRedirectAway &&
    !isEarlyMeetingFlow
  );
  useManualExposure({
    flagKey: 'release-onboarding-signup-bypass',
    isEnabled: isSignupBypassEnabled,
    isVisible: isBypassVisible,
  });

  // Capture referral code and campaign from URL on mount (before any redirects)
  // Store in both atom and localStorage to persist across auth redirects
  useEffect(() => {
    const code = getRefCodeFromUrl();
    const campaign = getReferralCampaignFromUrl();
    if (code) {
      setRefCode(code);
      storeRefCode(code);
    }
    if (campaign) {
      setReferralCampaign(campaign);
      storeReferralCampaign(campaign);
    }
  }, [setRefCode, setReferralCampaign]);

  useEffect(() => {
    // Wait for the router to hydrate query params — on the initial Pages Router
    // render `router.query` is `{}`, which would misclassify a `?flow=book-demo`
    // return as a normal auth'd visit and bounce the user to /scribe.
    if (!isReady) return;

    // Don't redirect if returning from social login in the demo booking flow
    if (isReturningFromBookDemo) return;

    if (importedTemplateId) {
      void importTemplate({ templateId: importedTemplateId });
    } else if (token && isAuthenticated) {
      void router.push(appendAttributionParamsToRedirectUrl('/scribe'));
    }
  }, [
    importTemplate,
    importedTemplateId,
    isAuthenticated,
    isReady,
    isReturningFromBookDemo,
    router,
    token,
  ]);

  if ((!isReady && !token) || (!flagsReady && !token)) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <Loader className="text-text-active" />
      </div>
    );
  }

  // Early meeting flow takes precedence over the v2 experiment AND the bypass
  // — when the flag is on, every cohort sees the choice-based landing with
  // demo booking. The "Sign up free" CTA inside EarlyMeetingFlow honors the
  // bypass flag itself.
  if (isEarlyMeetingFlow && !isTauri()) {
    return (
      <>
        <ForceUpdate />
        <EarlyMeetingFlow />
      </>
    );
  }

  // Marketing-attributed signup bypass — applies to any cohort that would
  // otherwise see the V2 sign-up form OR the legacy unauthenticated demo
  // flow. Variant-agnostic so we don't have to wait for the V2 onboarding
  // rollout (or any future V3 rollout) to turn on the bypass.
  //
  // The `!isTauri() && !isOnPrem` guards are defence-in-depth: the flag
  // function already short-circuits to `false` on both, and repeating them
  // here means desktop and on-prem keep the legacy in-app flow even if the
  // flag ever loses those internal checks. The Auth0 browser redirect in
  // `<RedirectToAuthSignup>` is not safe to fire under the Tauri auth plugin,
  // and cannot complete on-prem, which authenticates against Keycloak.
  if (isSignupBypassEnabled && !isTauri() && !isOnPrem) {
    return (
      <>
        <ForceUpdate />
        <RedirectToAuthSignup />
      </>
    );
  }

  // Onboarding v2 experiment: experimental = new flow, control = legacy flow
  if (IS_ONBOARDING_V2_ENABLED()) {
    const variant = GET_ONBOARDING_V2_VARIANT();

    if (variant === 'control') {
      return <UnauthenticatedDemoFlow />;
    }

    return (
      <>
        <ForceUpdate />
        <SignUpPage />
      </>
    );
  }

  return <UnauthenticatedDemoFlow />;
};

export default OnboardingPage;
